<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import Card from 'primevue/card';
import Toolbar from 'primevue/toolbar';
import { createModel } from '@kp/js-model';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { errToToast, reallyCancel } from '@/helpers/tools';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import KpButton from '@/components/KpButton.vue';
import KpNumberField from '@/components/KpNumberField.vue';
import KpWaermeerzeugerWarmwasserSelect from '@/components/KpWaermeerzeugerWarmwasserSelect.vue';
import { NutzungsgradWaermeerzeugerWarmwasser } from '@/models/NutzungsgradWaermeerzeugerWarmwasser';
import { createValidator, createValidators, validateNotEmpty } from '@/helpers/model_validators';
import InstallationsperiodeSelect from '@/components/InstallationsperiodeSelect.vue';

const props = defineProps<{
    // id is injected via router:
    id: number | 'new';
}>();

const router = useRouter();
const toast = useToast();
const record: Ref<NutzungsgradWaermeerzeugerWarmwasser | null> = ref(null);
const saving = ref(false);

const { allValid, validators } = createValidators({
    waermeerzeuger_warmwasser: createValidator(
        computed(() => record.value?.waermeerzeuger_warmwasser_id),
        [validateNotEmpty],
    ),
    installationsperiode: createValidator(
        computed(() => record.value?.installationsperiode_id),
        [validateNotEmpty],
    ),
    nutzungsgrad: createValidator(
        computed(() => record.value?.nutzungsgrad),
        [validateNotEmpty],
    ),
});

onMounted(() => {
    loadRecord(props.id);
});

onBeforeRouteLeave(() => {
    return reallyCancel(record?.value);
});

onBeforeRouteUpdate((to, from) => {
    if (to.params.id !== from.params.id) {
        loadRecord(to.params.id === 'new' ? 'new' : Number(to.params.id));
    }
});

async function loadRecord(id: number | 'new') {
    if (id === 'new') {
        record.value = createModel(NutzungsgradWaermeerzeugerWarmwasser);
    } else {
        try {
            const rec = await NutzungsgradWaermeerzeugerWarmwasser.loadById(id);
            if (rec) {
                record.value = rec;
            } else {
                record.value = null;
                throw new Error(getI18n().t('record_not_found'));
            }
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

async function onSave() {
    if (record?.value === null) {
        return;
    }
    saving.value = true;
    try {
        const isNew = !Number(record.value.id);
        await record.value.save();
        record.value.commit();
        toast.add({
            severity: 'success',
            detail: getI18n().t('datensatz_gespeichert'),
            life: 1000,
        });
        if (isNew) {
            router.push({ name: 'bg_geb_nutzungsgrad_warmwasser_edit', params: { id: record.value.id } });
        }
    } catch (e) {
        errToToast(e, toast);
    } finally {
        saving.value = false;
    }
}

function onCancel() {
    if (reallyCancel(record?.value)) {
        record?.value?.rollback();
        router.push({ name: 'bg_geb_nutzungsgrad_warmwasser' });
    }
}
</script>
<template>
    <div v-if="!record" class="flex justify-content-center">
        <ProgressSpinner></ProgressSpinner>
    </div>
    <div v-else>
        <Card class="kp-max-content-width">
            <template #header>
                <Toolbar class="toolbar">
                    <template #start>
                        <h2>
                            <i class="fas fa-user"></i> {{ $t('nutzungsgrade_warmwasser') }}:
                            {{ record.id || $t('new') }}
                        </h2>
                    </template>
                    <template #end>
                        <div class="flex gap-1">
                            <KpButton
                                severity="success"
                                icon="far fa-floppy-disk"
                                v-tooltip="$t('speichern')"
                                :loading="saving"
                                :disabled="!allValid"
                                @click="onSave"
                            />
                            <KpButton
                                severity="secondary"
                                icon="far fa-xmark"
                                v-tooltip="$t('abbrechen')"
                                :disabled="saving"
                                @click="onCancel"
                            />
                        </div>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="formgrid grid align-items-start">
                    <KpWaermeerzeugerWarmwasserSelect
                        v-model="record.waermeerzeuger_warmwasser_id"
                        class="col-6"
                        fluid
                        :label="$t('waermeerzeuger_warmwasser')"
                        :invalid="!validators.waermeerzeuger_warmwasser.isValid.value"
                        :error="validators.waermeerzeuger_warmwasser.msg.value"
                    ></KpWaermeerzeugerWarmwasserSelect>
                </div>
                <div class="formgrid grid align-items-start">
                    <InstallationsperiodeSelect
                        v-model="record.installationsperiode_id"
                        class="col-6"
                        fluid
                        :invalid="!validators.installationsperiode.isValid.value"
                        :error="validators.installationsperiode.msg.value"
                    ></InstallationsperiodeSelect>
                    <KpNumberField
                        class="col-6"
                        fluid
                        :label="$t('nutzungsgrad')"
                        :maxFractionDigits="5"
                        v-model="record.nutzungsgrad"
                        :invalid="!validators.nutzungsgrad.isValid.value"
                        :error="validators.nutzungsgrad.msg.value"
                    />
                </div>
            </template>
        </Card>
    </div>
</template>

<style lang="css" scoped>
.toolbar {
    border: none;
    border-bottom: 1px solid var(--p-toolbar-border-color);
    h2 {
        margin: 0;
    }
}
</style>
